import type { SerializedError } from '@reduxjs/toolkit';
import { useCallback, useMemo } from 'react';

import { SourceApp } from '@shared/constants/constants';
import { useLayoutViewContext } from '@shared/context';
import type { FederatedUserProfile } from '@shared/services/personalHealth/types/getFederatedUserProfile.types';
import type { ProfileBasic, ProfileFull } from '@shared/types/user.types';

import { useCurrentFederatedUser } from '../useCurrentFederatedUser';
import { useCurrentUserV2 } from '../useCurrentUserV2';

interface UseCurrentUserBaseReturn {
  clearUserProfile: () => void;
  error?: SerializedError;
  isFederatedProfile?: boolean;
  isLoading: boolean;
}

interface UseCurrentUserBasicProfileOrCurrentFederatedUserReturn extends UseCurrentUserBaseReturn {
  userProfile?: FederatedUserProfile | ProfileBasic;
}

interface UseCurrentUserOrCurrentFederatedUserParams<T extends boolean = false> {
  inSequenceCall?: boolean;
  isPatientMasterInfoRequired?: T;
  secure?: boolean;
  skip?: boolean;
}

type UseCurrentUserOrCurrentFederatedUserReturn<T extends boolean> = T extends true
  ? UseCurrentUserPatientMasterProfileOrCurrentFederatedUserReturn
  : UseCurrentUserBasicProfileOrCurrentFederatedUserReturn;

interface UseCurrentUserPatientMasterProfileOrCurrentFederatedUserReturn extends UseCurrentUserBaseReturn {
  userProfile?: FederatedUserProfile | ProfileFull;
}

const useCurrentUserOrCurrentFederatedUser = <T extends boolean = false>({
  isPatientMasterInfoRequired,
  skip,
  secure = false,
  inSequenceCall = false,
}: UseCurrentUserOrCurrentFederatedUserParams<T> = {}): UseCurrentUserOrCurrentFederatedUserReturn<T> => {
  const { isSharedServiceView, sourceApp } = useLayoutViewContext();
  const showFederatedUserProfile = isSharedServiceView && sourceApp !== SourceApp.NCA;
  const {
    isLoading: isLoadingUserProfile,
    userProfile: currentUserProfile,
    clearUserProfile,
    userProfileError,
  } = useCurrentUserV2({
    isPatientMasterInfoRequired,
    // Only one profile is ever used, so skip the other: its loading state would otherwise leak into `isLoading` without
    // its error being surfaced, letting a failing call flip callers back to a skeleton on every refetch (a fetch loop).
    skip: skip || showFederatedUserProfile,
    secure,
    inSequenceCall,
  });
  const {
    isLoading: isLoadingFederatedUserProfile,
    userProfile: federatedUserProfile,
    clearFederatedUserProfile,
    userProfileError: federatedUserProfileError,
  } = useCurrentFederatedUser({
    skip: !showFederatedUserProfile || skip,
  });

  const isLoadingProfile = showFederatedUserProfile ? isLoadingFederatedUserProfile : isLoadingUserProfile;
  const userProfileToUse = showFederatedUserProfile ? federatedUserProfile : currentUserProfile;
  const userProfileErrorToUse = showFederatedUserProfile ? federatedUserProfileError : userProfileError;

  const clearUserProfileToUse = useCallback(() => {
    clearFederatedUserProfile();
    clearUserProfile();
  }, [clearFederatedUserProfile, clearUserProfile]);

  const currentUserOrCurrentFederatedUserReturn = useMemo(
    () => ({
      isLoading: isLoadingProfile,
      userProfile: userProfileToUse,
      isFederatedProfile: showFederatedUserProfile,
      clearUserProfile: clearUserProfileToUse,
      error: userProfileErrorToUse,
    }),
    [isLoadingProfile, userProfileToUse, showFederatedUserProfile, clearUserProfileToUse, userProfileErrorToUse]
  );

  return currentUserOrCurrentFederatedUserReturn as UseCurrentUserOrCurrentFederatedUserReturn<T>;
};

export { useCurrentUserOrCurrentFederatedUser };
